import Footer from "./Footer"; // Import Footer component
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LabelList,
  ResponsiveContainer,
} from "recharts";
const introLogo = "/reportImages/intro-logo.png";
// import "./RimsSummary.css";

const CustomTick = (props) => {
  const { x, y, payload } = props;
  const name = payload.value;
  if (name?.includes("@")) {
    const [before, after] = name.split("@");
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="middle"
          fontSize={10}
          fontWeight={"bold"}
        >
          {before} at
          <tspan x={0} dy={12}>
            {after}
          </tspan>
        </text>
      </g>
    );
  }
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={16} textAnchor="middle" fontSize={10}>
        {name}
      </text>
    </g>
  );
};

const getRimsSummaryText = (rimsData) => {
  if (!rimsData?.length) return "";

  const increased = [];
  const decreased = [];

  rimsData.forEach((station) => {
    const change = station.change?.inflow ?? 0;
    if (change > 0) increased.push(station.Rims?.replace("@", " at "));
    else if (change < 0) decreased.push(station.Rims?.replace("@", " at "));
  });

  const total = rimsData.length;

  // Case 1: All increased
  if (increased.length === total && decreased.length === 0) {
    return "Inflows at all rim stations increased this week.";
  }

  // Case 2: All decreased
  if (decreased.length === total && increased.length === 0) {
    return "Inflows at all rim stations decreased this week.";
  }

  // Case 3: Mixed — decide majority
  if (increased.length > decreased.length) {
    return `Inflows at most rim stations increased while inflows at ${decreased} decreased this week.`;
  } else {
    return `Inflows at most rim stations decreased while inflows at ${increased} increased this week.`;
  }
};

const highlightChangeWord = (text) => {
  // Split into words, spaces, and punctuation
  return text.split(/(\s+|[.,;:!])/).map((part, index) => {
    const clean = part.replace(/[^a-zA-Z]/g, "");
    if (
      clean.toLowerCase() === "increased" ||
      clean.toLowerCase() === "increase"
    ) {
      return (
        <span key={index} className="increased">
          {part}
        </span>
      );
    }
    if (
      clean.toLowerCase() === "decreased" ||
      clean.toLowerCase() === "decrease"
    ) {
      return (
        <span key={index} className="decreased">
          {part}
        </span>
      );
    }
    return part;
  });
};

const RimsSummary = ({
  data,
  currentWeekLabel,
  previousWeekLabel,
  weekVsWeekLabel,
  currentWeek,
  previousWeek,
}) => {
  const chartData = data.map((item) => ({
    Rims: item.Rims,
    // Rims: item.Rims?.replace("@", " at "),
    currentWeekInflow: item.currentWeek?.inflow || 0,
    previousWeekInflow: item.previousWeek?.inflow || 0,
    currentWeekOutflow: item.currentWeek?.outflow || 0,
    previousWeekOutflow: item.previousWeek?.outflow || 0,
  }));

  return (
    <>
      <img src={introLogo} alt="PAR Logo" className="toc-logo" />
      <div style={{ borderBottom: "2px solid #ccc", margin: "0px 40px" }}></div>
      <div className="rims-section" id="rims-section">
        <h3 className="section-title">3c. Rims Summary</h3>
        <p className="summary-text">
          {highlightChangeWord(getRimsSummaryText(data))}
        </p>
        <h6 className="toc-heading">Rims Week on Week Summary</h6>
        <table className="table table-striped comparison-table">
          <thead>
            <tr>
              <th rowSpan={3} style={{ width: "100px" }}>
                Rims
              </th>
              <th colSpan={2} rowSpan={2} style={{ width: "170px" }}>
                <span>
                  {currentWeekLabel.split("\n").map((line, idx) => (
                    <div key={idx}>
                      {line}
                      <br />
                    </div>
                  ))}
                </span>
              </th>
              <th colSpan={2} rowSpan={2} style={{ width: "170px" }}>
                <span>
                  {previousWeekLabel.split("\n").map((line, idx) => (
                    <div key={idx}>
                      {line}
                      <br />
                    </div>
                  ))}
                </span>
              </th>
              <th colSpan={4}>Week on Week</th>
            </tr>
            <tr>
              <th colSpan={2}>Change</th>
              <th colSpan={2}>%</th>
            </tr>
            <tr>
              <th className="inflow-darkblue">Inflow</th>
              <th className="outflow-darkgreen">Outflow</th>
              <th className="inflow-lightblue">Inflow</th>
              <th className="inflow-lightgreen">Outflow</th>
              <th>Inflow</th>
              <th>Outflow</th>
              <th>Inflow</th>
              <th>Outflow</th>
            </tr>
          </thead>
          <tbody>
            {data.map((r, idx) => {
              const { inflow: cIn, outflow: cOut } = r.currentWeek;
              const { inflow: pIn, outflow: pOut } = r.previousWeek;
              const chIn = cIn - pIn;
              const chOut = cOut - pOut;
              const pctIn = r.percent_change?.inflow;
              const pctOut = r.percent_change?.outflow;

              // Helper to display numbers with color for negatives
              const display = (val, isPercent = false) => {
                if (val === null || val === undefined) return "-";
                const num = Number(val);
                if (isNaN(num)) return "-";
                const isNegative = num < 0 || Object.is(num, -0);
                return (
                  <span style={{ color: isNegative ? "#DC2626" : undefined }}>
                    {isNegative ? "-" : ""}
                    {Math.abs(num).toLocaleString(undefined, {
                      minimumFractionDigits: isPercent ? 2 : 0,
                      maximumFractionDigits: isPercent ? 2 : 2,
                    })}
                    {isPercent ? "%" : ""}
                  </span>
                );
              };

              // Helper for normal values (no color, just 0 for null/undefined/0)
              const displayNormal = (val) =>
                val === null || val === undefined || val === 0
                  ? "0"
                  : val.toLocaleString();

              return (
                <tr key={idx}>
                  <td>{r.Rims?.replace("@", " at ")}</td>
                  <td>{displayNormal(cIn)}</td>
                  <td>{displayNormal(cOut)}</td>
                  <td>{displayNormal(pIn)}</td>
                  <td>{displayNormal(pOut)}</td>
                  <td>{display(chIn)}</td>
                  <td>{display(chOut)}</td>
                  <td>
                    {pctIn === null || pctIn === undefined
                      ? "-"
                      : display(pctIn, true)}
                  </td>
                  <td>
                    {pctOut === null || pctOut === undefined
                      ? "-"
                      : display(pctOut, true)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="source-text">Source: Pakistan Agriculture Research</p>

        <div className="chart-row">
          <div className="chart-column">
            <span className="image-caption">
              Rims Inflow – {weekVsWeekLabel}
            </span>{" "}
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={chartData} margin={{ top: 35 }}>
                <XAxis
                  dataKey="Rims"
                  fontSize={10}
                  interval={0}
                  fontWeight={"bold"}
                  tick={<CustomTick />}
                />
                <YAxis
                  tickLine={false}
                  fontSize={9}
                  width={40}
                  stroke="#000000"
                  fontWeight={"bold"}
                  tickFormatter={(value) => value.toLocaleString()}
                />
                <Tooltip />
                <Legend
                  iconSize={8}
                  iconType="circle"
                  wrapperStyle={{ top: "30" }}
                  stroke="#000000"
                  strokeWidth={0.5}
                />
                <Bar
                  dataKey="currentWeekInflow"
                  fill="#2F8DCC"
                  name={`${currentWeek} Inflow`}
                >
                  <LabelList
                    dataKey="currentWeekInflow"
                    content={({ x, y, width, value }) => (
                      <text
                        stroke="#000000"
                        strokeWidth={0.3}
                        x={x + width / 2}
                        y={y - 2}
                        textAnchor="start"
                        transform={`rotate(-90, ${x + width / 2}, ${y - 5})`}
                        fontSize={8}
                      >
                        {value?.toLocaleString()}
                      </text>
                    )}
                  />
                </Bar>
                <Bar
                  dataKey="previousWeekInflow"
                  fill="#C5DFF1"
                  name={`${previousWeek} Inflow`}
                >
                  <LabelList
                    dataKey="previousWeekInflow"
                    content={({ x, y, width, value }) => (
                      <text
                        stroke="#000000"
                        strokeWidth={0.3}
                        x={x + width / 2}
                        y={y - 2}
                        textAnchor="start"
                        transform={`rotate(-90, ${x + width / 2}, ${y - 5})`}
                        fontSize={8}
                      >
                        {value?.toLocaleString()}
                      </text>
                    )}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="chart-column">
            <span className="image-caption">
              Rims Outflow – {weekVsWeekLabel}
            </span>{" "}
            <ResponsiveContainer width="100%" height={250}>
              {/* {console.log(chartData)} */}
              <BarChart data={chartData} margin={{ top: 35 }}>
                <XAxis
                  dataKey="Rims"
                  fontSize={10}
                  tick={<CustomTick />}
                  interval={0}
                />
                <YAxis
                  tickLine={false}
                  fontSize={9}
                  width={40}
                  stroke="#000000"
                  fontWeight={"bold"}
                  tickFormatter={(value) => value.toLocaleString()}
                />
                <Tooltip />
                <Legend
                  iconSize={8}
                  iconType="circle"
                  wrapperStyle={{ top: "30" }}
                />

                <Bar
                  dataKey="currentWeekOutflow"
                  fill="#599859"
                  name={`${currentWeek} Outflow`}
                >
                  <LabelList
                    dataKey="currentWeekOutflow"
                    content={({ x, y, width, value }) => (
                      <text
                        stroke="#000000"
                        strokeWidth={0.3}
                        x={x + width / 2}
                        y={y - 2}
                        textAnchor="start"
                        transform={`rotate(-90, ${x + width / 2}, ${y - 5})`}
                        fontSize={8}
                      >
                        {value?.toLocaleString()}
                      </text>
                    )}
                  />
                </Bar>
                <Bar
                  dataKey="previousWeekOutflow"
                  fill="#B7DEB7"
                  name={`${previousWeek} Outflow`}
                >
                  <LabelList
                    dataKey="previousWeekOutflow"
                    content={({ x, y, width, value }) => (
                      <text
                        stroke="#000000"
                        strokeWidth={0.3}
                        x={x + width / 2}
                        y={y - 2}
                        textAnchor="start"
                        transform={`rotate(-90, ${x + width / 2}, ${y - 5})`}
                        fontSize={8}
                      >
                        {value?.toLocaleString()}
                      </text>
                    )}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div style={{ marginTop: "350px" }}>
          <Footer footerNumber="7" footerText="Weekly Water & Weather Report" />
        </div>
      </div>
    </>
  );
};

export default RimsSummary;
